/**
 * BackendClient — Replaces the Supabase JS SDK.
 *
 * Auth and storage go through the backend's typed /api/v1, authenticated with X-Api-Key and the
 * session's bearer. User sessions are stored in localStorage.
 */

import createClient, { type Client } from 'openapi-fetch';
import type { components, paths } from '@/generated/contracts/api-v1';
import { v1, storageObjectUrl, toResult, sendToBackend, versionAtStart } from '@/infrastructure/api/v1/client';
import { uploadFile } from '@/infrastructure/api/v1/uploads';
import { listAll } from '@/infrastructure/api/v1/lists';
import { forgetReturnPath, rememberReturnPath } from '@/modules/auth/returnAfterLogin';
import { BACKEND_URL, API_KEY } from './backendEnv';
import { fetchWithResilience } from './resilientFetch';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface BackendUser {
  id: string;
  email?: string;
  user_metadata?: Record<string, any>;
  app_metadata?: Record<string, any>;
  /** True when signing in provisioned the user's org (an OAuth sign-up, not a returning login). */
  is_new_user?: boolean;
  [key: string]: any;
}

/** sessionStorage key holding the provider of an in-flight OAuth round-trip. */
export const OAUTH_PROVIDER_KEY = 'lureo_oauth_provider';

/** sessionStorage key: one-shot {provider, isNewUser} handoff for OAuth-return analytics. */
export const OAUTH_OUTCOME_KEY = 'lureo_oauth_outcome';

/** sessionStorage key: marketing opt-in chosen before an OAuth round-trip. The backend
 * provisions OAuth users with marketing_consent=false, so this is applied on return for
 * new users who opted in. Survives the redirect via sessionStorage. */
export const OAUTH_MARKETING_CONSENT_KEY = 'lureo_oauth_marketing_consent';

export interface BackendSession {
  access_token: string;
  refresh_token: string;
  expires_in?: number;
  /** When the access token runs out, in seconds by this machine's clock (set when the session is stored). */
  expires_at?: number;
  user: BackendUser;
}

// USER_CHANGED: another tab stored a different user's session
type AuthEvent = 'SIGNED_IN' | 'SIGNED_OUT' | 'TOKEN_REFRESHED' | 'INITIAL_SESSION' | 'USER_CHANGED';
type AuthCallback = (event: AuthEvent, session: BackendSession | null) => void;

const SESSION_KEY = 'lureo_session';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** The signed-in user's id, or null. */
export const currentUserId = (): string | null => getStoredSession()?.user?.id ?? null;

function getStoredSession(): BackendSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function storeSession(session: BackendSession | null) {
  if (session) {
    localStorage.setItem(SESSION_KEY, JSON.stringify({ ...session, expires_at: session.expires_at ?? expiresAtFrom(session.access_token) }));
  } else {
    localStorage.removeItem(SESSION_KEY);
  }
  refusedRefreshToken = undefined;
  scheduleRefreshAhead();
}

/** The access token's own times, in seconds; none for a token that is not a JWT. */
function tokenTimes(token: string): { exp?: number; iat?: number } {
  try {
    const { exp, iat } = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return { exp: typeof exp === 'number' ? exp : undefined, iat: typeof iat === 'number' ? iat : undefined };
  } catch {
    return {};
  }
}

// The token's lifetime from now, so a clock that disagrees with the backend's still refreshes it in time
function expiresAtFrom(token: string, now = Date.now()): number | undefined {
  const { exp, iat } = tokenTimes(token);
  return exp !== undefined && iat !== undefined ? Math.floor(now / 1000) + exp - iat : exp;
}

/** When the session's token runs out, in ms by this machine's clock; null when it doesn't say. */
function sessionExpiresAt(session: BackendSession | null): number | null {
  if (!session?.access_token) return null;
  const at = session.expires_at ?? tokenTimes(session.access_token).exp;
  return at === undefined ? null : at * 1000;
}

// The user this tab's pages were built for; localStorage is shared, so another tab can store someone else's session
let tabUserId: string | null = getStoredSession()?.user?.id ?? null;

const storedUserIsTabUser = () => !tabUserId || getStoredSession()?.user?.id === tabUserId;

/**
 * A refresh's outcome: the new session or none, whether set-session refused the refresh token, and
 * the refresh token the session now in question holds (the new one, or the one tried).
 */
interface RefreshOutcome {
  session: BackendSession | null;
  rejected: boolean;
  refreshToken?: string;
}

let refreshPromise: Promise<RefreshOutcome> | null = null;

// The refresh token set-session last refused: its session is over, so it is never sent again
let refusedRefreshToken: string | undefined;

// Every tab waits on the refresh lock, so a refresh that hangs must give up
const REFRESH_TIMEOUT_MS = 15_000;

// Every tab holds the same session, so they expire together: one refreshes while the others wait, then use what it stored
async function withRefreshLock<T>(work: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks) return work();
  let result!: T;
  await locks.request('lureo-session-refresh', async () => {
    result = await work();
  });
  return result;
}

// Only a 401 (refresh_token_invalid) ends the session; a 400, 429, 5xx (503 auth_unavailable) or
// network error leaves it to retry later.
async function refreshSession(): Promise<RefreshOutcome> {
  // Deduplicate concurrent refresh calls
  if (refreshPromise) return refreshPromise;

  // Checked before the shared promise exists: returning early inside it skipped the finally, so
  // every later refresh in the page's life resolved to this null
  const session = getStoredSession();
  if (!session?.refresh_token || session.refresh_token === refusedRefreshToken) {
    return { session: null, rejected: true, refreshToken: session?.refresh_token };
  }

  refreshPromise = (async (): Promise<RefreshOutcome> => {
    try {
      return await withRefreshLock(() => refreshStored(session));
    } catch {
      return { session: null, rejected: false, refreshToken: session.refresh_token };
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

async function refreshStored(session: BackendSession): Promise<RefreshOutcome> {
  const current = getStoredSession();
  if (!current?.refresh_token) return { session: null, rejected: true, refreshToken: session.refresh_token };
  if (current.refresh_token !== session.refresh_token) {
    // Another tab refreshed while this one waited; a different user's session is never taken for this one's
    if (current.user?.id !== session.user?.id) return { session: null, rejected: false, refreshToken: session.refresh_token };
    return { session: current, rejected: false, refreshToken: current.refresh_token };
  }

  const { data, error } = await toResult(v1App.POST('/api/v1/sessions/refresh', {
    body: { refreshToken: current.refresh_token },
    signal: AbortSignal.timeout(REFRESH_TIMEOUT_MS),
  }));
  if (error) {
    if (error.status === 401) refusedRefreshToken = current.refresh_token;
    return { session: null, rejected: error.status === 401, refreshToken: current.refresh_token };
  }
  if (!data.accessToken) return { session: null, rejected: false, refreshToken: current.refresh_token };

  const newSession: BackendSession = {
    access_token: data.accessToken,
    refresh_token: data.refreshToken || current.refresh_token,
    user: current.user,
  };
  storeSession(newSession);
  notifyListeners('TOKEN_REFRESHED', newSession);
  return { session: newSession, rejected: false, refreshToken: newSession.refresh_token };
}

// A token this close to running out is refreshed before it is sent, so no request goes out with a bearer the backend will
// refuse. Wide enough for a hidden tab, whose timers a browser may run up to a minute late
export const REFRESH_AHEAD_MS = 2 * 60_000;
// A refresh ahead of time that failed for a reason that passes is tried again this much later
const REFRESH_AHEAD_RETRY_MS = 30_000;

/** The stored session's refresh when its token runs out within REFRESH_AHEAD_MS; null when it has longer or there is none. */
function refreshIfExpiring(now = Date.now()): Promise<RefreshOutcome> | null {
  const expiresAt = sessionExpiresAt(getStoredSession());
  return expiresAt !== null && expiresAt - now <= REFRESH_AHEAD_MS ? refreshSession() : null;
}

let refreshAheadTimer: ReturnType<typeof setTimeout> | undefined;

// The stored token is refreshed before it runs out even with no request to send, so the editor's renderer, which holds
// the token it was handed (setJwt) and never refreshes it, gets the new one in time
function scheduleRefreshAhead(delayMs?: number) {
  clearTimeout(refreshAheadTimer);
  refreshAheadTimer = undefined;
  const expiresAt = sessionExpiresAt(getStoredSession());
  if (expiresAt === null) return;
  refreshAheadTimer = setTimeout(async () => {
    const outcome = await refreshIfExpiring();
    // A new session schedules its own refresh when it is stored
    if (outcome && !outcome.session && !outcome.rejected) scheduleRefreshAhead(REFRESH_AHEAD_RETRY_MS);
  }, delayMs ?? Math.max(0, expiresAt - REFRESH_AHEAD_MS - Date.now()));
}

/** The device's id as PostHog knows it, on every backend call so the backend's timing of it can be told apart per
 * person; absent until PostHog has loaded (and never with no key, or PostHog turned off for the build). PostHog's setup
 * hands it over (setDeviceIdSource), so this layer doesn't depend on posthog-js. Letters, digits, - and _, at most 64. */
export const DEVICE_HEADER = 'X-Lureo-Device';

let deviceIdSource: (() => string | null | undefined) | null = null;

// The id deviceId() last gave, and who is told when it changes (the New Relic agent, which labels the tab with it)
let lastDeviceId: string | null = null;
const deviceIdListeners = new Set<(id: string | null) => void>();

export function setDeviceIdSource(source: (() => string | null | undefined) | null): void {
  deviceIdSource = source;
  // Its first id is told now, not with the next backend call
  deviceId();
}

function readDeviceId(): string | null {
  try {
    const id = deviceIdSource?.();
    const clean = typeof id === 'string' ? id.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 64) : '';
    return clean.length > 0 ? clean : null;
  } catch {
    return null;
  }
}

/** The device id the next backend call carries (DEVICE_HEADER); a change from the last one read is told (onDeviceIdChange). */
export function deviceId(): string | null {
  const id = readDeviceId();
  if (id !== lastDeviceId) {
    lastDeviceId = id;
    for (const listener of [...deviceIdListeners]) {
      try {
        listener(id);
      } catch {
        // A listener that fails never fails the call that read the id
      }
    }
  }
  return id;
}

/**
 * Calls `listener` with the device id each time the one the app sends changes (PostHog loading, identifying the person,
 * or resetting), as it is read for a backend call, before that call goes out, so whatever labels the tab with it says
 * what the backend was sent. Returns what stops it.
 */
export function onDeviceIdChange(listener: (id: string | null) => void): () => void {
  const each = (id: string | null) => listener(id);
  deviceIdListeners.add(each);
  return () => void deviceIdListeners.delete(each);
}

/** This tab's id, sent on every v1 request so the scene change stream can tell this tab's own writes apart. */
export const CLIENT_SESSION_HEADER = 'X-Lureo-Client-Session';
export const CLIENT_SESSION = crypto.randomUUID();

/** The app key and this tab's id, then the caller's headers, then the stored session's bearer unless `bearer` is false. */
export function withAuthHeaders(headers?: HeadersInit, bearer = true): Headers {
  const merged = new Headers({ 'Content-Type': 'application/json', 'X-Api-Key': API_KEY, [CLIENT_SESSION_HEADER]: CLIENT_SESSION });
  const device = deviceId();
  if (device) merged.set(DEVICE_HEADER, device);
  new Headers(headers).forEach((value, key) => merged.set(key, value));
  const token = getStoredSession()?.access_token;
  if (!bearer) merged.delete('Authorization');
  else if (token) merged.set('Authorization', `Bearer ${token}`);
  return merged;
}

/**
 * The typed /api/v1 client for the session calls themselves (sign in, refresh, sign up, reset, sign out):
 * the app key alone, never the stored bearer, and no refresh or retry, so a refused password is never
 * taken for an expired session. Its version read, the same for every caller, is answered from the session bootstrap
 * while the app starts (versionAtStart).
 */
export const v1App = createClient<paths>({
  baseUrl: BACKEND_URL,
  fetch: async request => (await versionAtStart(request)) ?? sendToBackend(request, { headers: withAuthHeaders(request.headers, false) }),
});

// For a session not stored yet or about to be dropped: no refresh, and a timeout on writes so signing in or out never hangs
function bearerClient(accessToken: string, writeTimeoutMs: number): Client<paths> {
  return createClient<paths>({
    baseUrl: BACKEND_URL,
    fetch: request => {
      const headers = withAuthHeaders(request.headers, false);
      headers.set('Authorization', `Bearer ${accessToken}`);
      return sendToBackend(request, { headers }, (input, init) => fetchWithResilience(input, init, request.method === 'GET' ? undefined : writeTimeoutMs));
    },
  });
}

const ENSURE_TIMEOUT_MS = 15_000;
const ENSURE_RETRY_MS = [1_000, 3_000];
const SIGN_OUT_TIMEOUT_MS = 5_000;

const isTransient = (status: number) => status === 0 || status === 429 || status >= 500;

async function ensureOrgWith(client: Client<paths>) {
  for (let attempt = 0; ; attempt++) {
    const result = await toResult(client.POST('/api/v1/orgs/ensure'));
    if (!result.error || !isTransient(result.error.status) || attempt >= ENSURE_RETRY_MS.length) return result;
    await new Promise(resolve => setTimeout(resolve, ENSURE_RETRY_MS[attempt]));
  }
}

export const ensureMyOrg = () => ensureOrgWith(v1);

async function errorMessage(response: Response): Promise<{ message: string; errorCode?: string }> {
  try {
    const body = await response.clone().json();
    return { message: body.message || body.error || body.msg || `HTTP ${response.status}`, errorCode: body.error_code };
  } catch {
    return { message: `HTTP ${response.status}` };
  }
}

async function isAuthFailure(response: Response): Promise<boolean> {
  if (response.ok) return false;
  if (response.status === 401) return true;
  const { message, errorCode } = await errorMessage(response);
  return errorCode === 'bad_jwt' || message === 'No authenticated user';
}

type Transport = (input: string | Request, init: RequestInit) => Promise<Response>;

export interface AuthorizedFetchOptions {
  /** Performs each attempt (default: fetch). */
  send?: Transport;
  /**
   * GETs only: when the session cannot be recovered, retry once with the app key alone instead of
   * sending the user to login, so public content stays readable.
   */
  anonymousReadFallback?: boolean;
}

/**
 * Drops the stored session and reports SIGNED_OUT, unless it no longer holds `failedRefreshToken`:
 * a sign-in that landed meanwhile (another tab) is kept. False when a different session is kept.
 */
function dropSession(failedRefreshToken: string | undefined): boolean {
  const stored = getStoredSession();
  if (!stored) {
    // Signed out elsewhere: this tab still shows the user until it hears so
    if (tabUserId) notifyListeners('SIGNED_OUT', null);
    return true;
  }
  if (stored.refresh_token !== failedRefreshToken) return false;
  const { pathname = '', search = '', hash = '' } = window.location;
  rememberReturnPath(pathname + search + hash, stored.user?.id ?? null);
  storeSession(null);
  notifyListeners('SIGNED_OUT', null);
  return true;
}

/**
 * Sends a request with the app key and the stored session's bearer, refreshing first a token about to run out
 * (REFRESH_AHEAD_MS). On an auth failure it refreshes
 * the token and retries once. When set-session refuses the refresh token, or the retry is refused
 * too, the session is dropped and the user sent to login; a GET with `anonymousReadFallback` instead
 * drops the session without the redirect and is sent once more without the bearer. When the refresh
 * fails for another reason (network, 400, 429, 5xx) the session is kept and the first response returned.
 * A session that replaced the failed one meanwhile is never dropped.
 */
export async function authorizedFetch(
  input: string | Request,
  init: RequestInit = {},
  { send = (request, requestInit) => fetch(request, requestInit), anonymousReadFallback = false }: AuthorizedFetchOptions = {},
): Promise<Response> {
  const method = (init.method ?? (typeof input === 'string' ? 'GET' : input.method)).toUpperCase();
  await refreshIfExpiring();
  // Checked after the refresh, which can wait on another tab's: that tab may have stored someone else's session meanwhile
  if (method !== 'GET' && method !== 'HEAD' && !storedUserIsTabUser()) return sessionChangedResponse();

  let sentBearer = false;
  const attempt = (bearer = true) => {
    const headers = withAuthHeaders(typeof input === 'string' ? init.headers : input.headers, bearer);
    // A multipart body sets its own Content-Type, boundary included
    if (init.body instanceof FormData) headers.delete('Content-Type');
    sentBearer = headers.has('Authorization');
    return typeof input === 'string' ? send(input, { ...init, headers }) : send(input.clone(), { ...init, headers });
  };

  let response = await attempt();
  if (!(await isAuthFailure(response))) return response;

  // Force a refresh and retry once before giving up: the token went stale anyway (clock skew, a revoked session,
  // backend-side rotation)
  const refreshed = await refreshSession();
  if (refreshed.session) {
    response = await attempt();
    if (!(await isAuthFailure(response))) return response;
  }

  // A refresh that failed for a reason that passes keeps the session; an anonymous answer is never taken for the user's own
  if (!refreshed.session && !refreshed.rejected) return response;

  if (anonymousReadFallback && method === 'GET') {
    if (refreshed.rejected) dropSession(refreshed.refreshToken);
    // A request sent without a bearer gains nothing from being sent again without one
    return sentBearer ? attempt(false) : response;
  }

  if (dropSession(refreshed.refreshToken)) window.location.href = '/login';
  return response;
}

const SESSION_CHANGED_TITLE = 'You signed in or out in another tab. Reload the page to continue.';

// A write from a tab whose user is no longer the stored one would land on the other account
function sessionChangedResponse(): Response {
  return new Response(JSON.stringify({ type: 'about:blank', status: 409, code: 'session_changed', title: SESSION_CHANGED_TITLE }), {
    status: 409,
    headers: { 'Content-Type': 'application/problem+json' },
  });
}

// ─── Auth State Listeners ────────────────────────────────────────────────────

const listeners = new Set<AuthCallback>();

function notifyListeners(event: AuthEvent, session: BackendSession | null) {
  if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') tabUserId = session?.user?.id ?? null;
  listeners.forEach(cb => {
    try { cb(event, session); } catch { /* swallow */ }
  });
}

// Another tab signed in, out, or refreshed the shared session
function onStoredSessionChanged(event: StorageEvent) {
  if (event.key !== null && event.key !== SESSION_KEY) return;
  scheduleRefreshAhead();
  const stored = getStoredSession();
  const storedUserId = stored?.user?.id ?? null;
  if (storedUserId === tabUserId) {
    if (stored) notifyListeners('TOKEN_REFRESHED', stored);
  } else if (!tabUserId) {
    notifyListeners('SIGNED_IN', stored);
  } else if (!stored) {
    notifyListeners('SIGNED_OUT', null);
  } else {
    notifyListeners('USER_CHANGED', stored);
  }
}

if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('storage', onStoredSessionChanged);
}

// A sleeping machine runs no timers, so a page shown again checks its token at once
if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') scheduleRefreshAhead();
  });
}

scheduleRefreshAhead();

// ─── Auth Client ─────────────────────────────────────────────────────────────

/** The signed-in user as the session keeps it, from GET /api/v1/users/me. */
function sessionUser(user: components['schemas']['UserDto'], isNewUser?: boolean): BackendUser {
  return {
    id: user.uniqueId,
    email: user.email ?? undefined,
    user_metadata: (user.userMetadata ?? undefined) as Record<string, any> | undefined,
    ...(isNewUser === undefined ? {} : { is_new_user: isNewUser }),
  };
}

export const BackendAuth = {
  async signInWithPassword({ email, password }: { email: string; password: string }) {
    const { data, error } = await toResult(v1App.POST('/api/v1/sessions', { body: { email, password } }));
    if (error) return { data: { user: null, session: null }, error: { message: error.message } };

    const user: BackendUser = { id: data.userUniqueId, email: data.email ?? undefined };
    const session: BackendSession = {
      access_token: data.accessToken,
      refresh_token: data.refreshToken,
      user,
    };
    storeSession(session);
    notifyListeners('SIGNED_IN', session);
    return { data: { user, session }, error: null };
  },

  async signInWithOAuth({ provider, options, marketingConsent }: { provider: components['schemas']['OAuthProvider']; options?: { redirectTo?: string; queryParams?: Record<string, string> }; marketingConsent?: boolean }) {
    // redirectTo is the client's own landing target; the backend's OAuth callback bounces the
    // session here in the URL fragment once the provider round-trip completes
    const target = options?.redirectTo || window.location.origin;
    const { data, error } = await toResult(v1App.POST('/api/v1/oauth-authorizations', { body: { provider, redirectTo: target } }));

    if (error) return { data: { url: null }, error: { message: error.message } };
    // Remember the provider so the analytics layer can tag the sign-up/login event
    // once the provider round-trip bounces back, plus the marketing opt-in so a new
    // OAuth user's consent is applied on return. Survives the redirect via sessionStorage.
    try {
      sessionStorage.setItem(OAUTH_PROVIDER_KEY, provider);
      sessionStorage.setItem(OAUTH_MARKETING_CONSENT_KEY, marketingConsent ? 'true' : 'false');
    } catch { /* best-effort */ }
    return { data: { url: data.authorizeUrl }, error: null };
  },

  async signOut() {
    // The bearer lets the server revoke this session's refresh token; signing out here never depends on it
    const stored = getStoredSession();
    if (stored?.access_token) await toResult(bearerClient(stored.access_token, SIGN_OUT_TIMEOUT_MS).DELETE('/api/v1/sessions/current'));
    storeSession(null);
    forgetReturnPath();
    notifyListeners('SIGNED_OUT', null);
    return { error: null };
  },

  async getSession() {
    const session = getStoredSession();
    return { data: { session }, error: null };
  },

  async getUser() {
    const session = getStoredSession();
    if (!session) return { data: { user: null }, error: null };

    const { data, error } = await toResult(v1.GET('/api/v1/users/me'));
    if (error) return { data: { user: null }, error: { message: error.message } };
    return { data: { user: sessionUser(data) }, error: null };
  },

  async setSession(
    { access_token, refresh_token }: { access_token: string; refresh_token: string },
    { mayCreateOrg }: { mayCreateOrg?: () => Promise<boolean> } = {},
  ): Promise<{ data: { session: BackendSession | null }; error: { message: string; code: string } | null }> {
    const asUser = bearerClient(access_token, ENSURE_TIMEOUT_MS);
    const failed = (error: { message: string; code: string }) => ({ data: { session: null }, error });

    const { data: user, error } = await toResult(asUser.GET('/api/v1/users/me'));
    if (error) return failed({ message: error.message, code: error.code });

    let isNewUser = false;
    if (!user.hasOrg) {
      if (mayCreateOrg && !(await mayCreateOrg())) return failed({ message: 'Sign-up is closed.', code: 'sign_up_closed' });
      const ensured = await ensureOrgWith(asUser);
      if (ensured.error) return failed({ message: ensured.error.message, code: ensured.error.code });
      isNewUser = ensured.data?.created === true;
    }

    const session: BackendSession = { access_token, refresh_token, user: sessionUser(user, isNewUser) };
    storeSession(session);
    notifyListeners('SIGNED_IN', session);
    return { data: { session }, error: null };
  },

  async updateUser(data: { email?: string; password?: string; data?: Record<string, any> }) {
    const { data: user, error } = await toResult(v1.PATCH('/api/v1/users/me', {
      body: { email: data.email, password: data.password, userMetadata: data.data },
    }));
    if (error) return { data: { user: null }, error: { message: error.message } };
    return { data: { user: sessionUser(user) }, error: null };
  },

  async resetPasswordForEmail(email: string, options?: { redirectTo?: string }) {
    const { error } = await toResult(v1App.POST('/api/v1/password-resets', { body: { email, redirectTo: options?.redirectTo } }));
    return { data: null, error: error ? { message: error.message } : null };
  },

  onAuthStateChange(callback: AuthCallback) {
    listeners.add(callback);

    // Fire initial session
    const session = getStoredSession();
    setTimeout(() => callback('INITIAL_SESSION', session), 0);

    return {
      data: {
        subscription: {
          unsubscribe: () => { listeners.delete(callback); },
        },
      },
    };
  },
};

// ─── Storage Client ──────────────────────────────────────────────────────────

export const BackendStorage = {
  from(bucket: string) {
    return {
      // Answers where the file was stored: its name gets a version, so the caller keeps this path and url, never one
      // built from the path it asked for. An explicit content type re-types the bytes; nothing is ever written over,
      // so upsert and cacheControl have no say
      async upload(path: string, file: Blob | File, options?: any) {
        const { data, error } = await uploadFile(bucket, path, file, options?.contentType);
        return error ? { data: null, error: { message: error.message } } : { data, error: null };
      },

      // A redirect that needs no credentials, so an <img src> or a loader can use it. The object is named in the
      // path, as a public URL always was: a query is the caller's (a cache version), and the file name comes last
      getPublicUrl(path: string) {
        return { data: { publicUrl: storageObjectUrl(bucket, path) } };
      },

      async remove(paths: string[]) {
        const { error } = await toResult(v1.DELETE('/api/v1/storage-objects', { params: { query: { bucket, path: paths } } }));
        return { data: null, error: error ? { message: error.message } : null };
      },

      async list(prefix: string) {
        const { data, error } = await listAll(page => v1.GET('/api/v1/storage-objects', { params: { query: { bucket, prefix, ...page } } }));
        return { data: data ?? [], error: error ? { message: error.message } : null };
      },
    };
  },
};

// ─── Unified Client (drop-in replacement for SbClient) ──────────────────────

export const BackendClient = {
  auth: BackendAuth,
  storage: BackendStorage,
};

export { BACKEND_URL };